import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RedirectFunction, Router, RouterLink } from '@angular/router';
import { map } from 'rxjs';

import { KsQueueComponent } from '../kickstart/queue/ks-queue';
import { DfFacilityQueueComponent } from '../df/facility-queue/df-facility-queue';

type Product = 'ks' | 'df';

const TABS: { value: Product; label: string }[] = [
  { value: 'ks', label: 'Kickstart Cash' },
  { value: 'df', label: 'Debtors Finance' },
];

/**
 * `/ops/kickstart` → `/ops/applications?product=ks`, carrying `?stage=` (the
 * admin dashboard's stage cards link there) and any other query param through.
 */
export const redirectKickstartQueue: RedirectFunction = ({ queryParams }) =>
  inject(Router).createUrlTree(['/ops/applications'], {
    queryParams: { ...queryParams, product: 'ks' },
  });

/**
 * The Ops *Applications* page (FLOW-346): every submitted application, one
 * product per tab, newest first, no cap.
 *
 * It used to render the dashboard's *Next actions* worklist, which stops at
 * twelve — so the thirteenth open file was on no Ops list at all (UI-046 §1;
 * DOC-46 §5: a review task is never hidden). The dashboard keeps that worklist
 * as its summary; this page owns the full lists, each the product's own queue
 * component with its own paging and URL filter. The tab is `?product=ks|df`.
 */
@Component({
  selector: 'app-applications-queue',
  imports: [RouterLink, KsQueueComponent, DfFacilityQueueComponent],
  templateUrl: './applications-queue.html',
  styleUrl: './applications-queue.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationsQueueComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly tabs = TABS;

  protected readonly product = toSignal(
    this.route.queryParamMap.pipe(map((p): Product => (p.get('product') === 'df' ? 'df' : 'ks'))),
    { initialValue: 'ks' as Product },
  );

  /** Switching product drops the other list's filter (?stage= / ?status=). */
  protected select(product: Product): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { product } });
  }
}
